import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  useMemo,
} from "react";
import {
  Col,
  Descriptions,
  Divider,
  Input,
  InputNumber,
  Radio,
  Row,
  Slider,
  Spin,
  Tooltip,
  Typography,
  Select,
  Button,
} from "antd";
import { Config, ModelConfig } from "../../model_gen/config";
import { ConfigService } from "../../services/ConfigService";
import { usePermissions } from "../../PermissionsProvider";
import { UpOutlined, DownOutlined } from "@ant-design/icons";
import CustomSwitch from "../shared/switch/CustomSwitch";
import {
  ConfigurationPanelProps,
  ConfigurationPanelRef,
} from "../../types/IApplication";
import { LinkButton } from "../shared/button/Buttons";
import { InfoCircleOutlined } from "@ant-design/icons";
import { InferenceLocations } from "../../types/IConfig";
import ModelInput from "../shared/input/ModelInput";

const ConfigurationPanel = forwardRef<
  ConfigurationPanelRef,
  ConfigurationPanelProps
>((props, ref) => {
  const { apiKey } = usePermissions();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [models, setModels] = useState<ModelConfig>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [unsavedConfig, setUnsavedConfig] = useState<Partial<Config> | null>(
    null
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Create a merged config that combines props.config with unsavedConfig
  const currentConfig = useMemo(() => {
    if (!props.config) return null;
    if (!unsavedConfig) return props.config;
    return {
      ...props.config,
      ...unsavedConfig,
    };
  }, [props.config, unsavedConfig]);

  const getModels = () => {
    setIsLoading(true);
    ConfigService.getModels(
      props?.intentGroupId ?? -1,
      setIsLoading,
      apiKey
    ).subscribe(
      (models: ModelConfig) => {
        // Sort the models in alphabetical order
        models.client_models.sort();
        models.server_models.sort();
        setModels(models);
      },
      (error: any) => {
        setIsLoading(false);
        console.error(error);
      }
    );
  };

  const updateConfig = (new_config: Partial<Config>, save: boolean = false) => {
    if (save) {
      setIsSaving(true);
      props.updateConfig(
        new_config,
        (loading: boolean) => {
          setIsSaving(loading);
          if (!loading) {
            setUnsavedConfig(null);
          }
        },
        true
      );
    } else {
      setUnsavedConfig((prev) => ({
        ...prev,
        ...new_config,
      }));
    }
  };

  const handleSaveChanges = () => {
    if (unsavedConfig) {
      updateConfig(unsavedConfig, true);
    }
  };

  useEffect(() => {
    getModels();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setTooltipOpen(true);
    }, 100);

    return () => clearTimeout(timeout);
  }, []);

  useImperativeHandle(ref, () => ({
    closeToolTipEvent() {
      setTooltipOpen(false);
    },
    openToolTipEvent() {
      setTimeout(() => {
        setTooltipOpen(true);
      }, 100);
    },
  }));

  const renderTooltip = (
    title: string,
    content: string,
    isOnLeft: boolean = false
  ) => (
    <span
      style={{
        position: "relative",
        paddingLeft: isOnLeft ? "20px" : "0",
        paddingRight: isOnLeft ? "0" : "20px",
      }}
      className="configuration-panel-label">
      <Tooltip title={content}>
        <InfoCircleOutlined
          style={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            left: isOnLeft ? "0" : "auto",
            right: isOnLeft ? "auto" : "0",
            color: "rgba(0, 0, 0, 0.45)",
            fontSize: "14px",
          }}
        />
      </Tooltip>
      {title}
    </span>
  );

  const hasO3Model = (config?: Config) => {
    const primaryHasO3 = config?.model?.includes("o3");
    const secondaryHasO3 =
      config?.fallback_config?.model?.includes("o3") ||
      config?.fallback_model?.includes("o3");
    return primaryHasO3 || secondaryHasO3;
  };

  return (
    <div
      className="overlay-container"
      style={{
        paddingTop: props.isModalOpen ? 16 : 0,
      }}>
      {(isLoading || !currentConfig) && (
        <div className="overlay">
          <Spin size="large" />
        </div>
      )}

      <div
        className={
          !props.isModalOpen
            ? "configuration-panel-wrapper"
            : "configuration-panel-wrapper-modal"
        }
        style={{ position: "relative", paddingBottom: "60px" }}>
        <div>
          {!props.isModalOpen ? (
            <Typography.Title
              level={3}
              style={{ margin: "0 0 24px 0", fontWeight: 400 }}>
              General
            </Typography.Title>
          ) : (
            <></>
          )}
          <Col span={24}>
            <div className="configure-menu-list-item-wrap">
              <div>
                <span className="configuration-panel-label">
                  {renderTooltip(
                    "Inference Location:",
                    "Choose where the model inference will take place",
                    true
                  )}
                </span>
              </div>
              <div>
                <Tooltip
                  title={
                    currentConfig?.apply_corrections
                      ? "Disable corrections to change inference location"
                      : ""
                  }>
                  <Radio.Group
                    className="custom-radio-btn"
                    onChange={(e) => {
                      if (!props.readOnly) {
                        updateConfig({
                          inference_location: e.target.value,
                          model:
                            e.target.value == InferenceLocations.CLIENT
                              ? models?.default_client_model
                              : currentConfig?.model,
                          context_retrieval_enabled:
                            e.target.value == InferenceLocations.SERVER
                              ? currentConfig?.context_retrieval_enabled
                              : false,
                        });
                      }
                    }}
                    value={currentConfig?.inference_location}
                    disabled={props.readOnly || isSaving}>
                    <Radio value={InferenceLocations.CLIENT}>Client</Radio>
                    <Radio value={InferenceLocations.SERVER}>Server</Radio>
                  </Radio.Group>
                </Tooltip>
              </div>
            </div>
          </Col>
          <Divider
            style={{
              borderColor: "#ECEDEC",
              margin: props.isModalOpen ? "16px 0" : "24px 0",
            }}
          />
          <Col span={24}>
            <div className="configure-menu-list-item-wrap">
              <div>
                <span className="configuration-panel-label">
                  {renderTooltip(
                    "Evaluations:",
                    "Enable or disable model evaluations",
                    true
                  )}
                </span>
              </div>
              <div className="switch-btn-wrap">
                <CustomSwitch
                  className="custom-switch"
                  checked={currentConfig?.evaluation_enabled}
                  disabled={props.readOnly || isSaving}
                  onChange={(checked) => {
                    if (!props.readOnly) {
                      updateConfig({
                        evaluation_enabled: checked,
                        apply_corrections: !checked
                          ? false
                          : currentConfig?.apply_corrections,
                      });
                    }
                  }}
                />
              </div>
            </div>
          </Col>
          <Divider
            style={{
              borderColor: "#ECEDEC",
              margin: props.isModalOpen ? "16px 0" : "24px 0",
            }}
          />
          <Col span={24}>
            <div className="configure-menu-list-item-wrap">
              <div>
                <span className="configuration-panel-label">
                  {renderTooltip(
                    "Apply Corrections:",
                    "Automatically apply corrections based on evaluations",
                    true
                  )}
                </span>
              </div>
              <div className="switch-btn-wrap">
                <Tooltip
                  title={
                    currentConfig?.evaluation_enabled
                      ? ""
                      : "Enable evaluations to apply corrections"
                  }>
                  <CustomSwitch
                    className="custom-switch"
                    checked={currentConfig?.apply_corrections}
                    disabled={props.readOnly || isSaving}
                    onChange={(checked) => {
                      if (!props.readOnly) {
                        updateConfig({
                          apply_corrections: checked,
                          inference_location: checked
                            ? InferenceLocations.SERVER
                            : currentConfig?.inference_location,
                          evaluation_enabled: checked
                            ? true
                            : currentConfig?.evaluation_enabled,
                        });
                      }
                    }}
                  />
                </Tooltip>
              </div>
            </div>
          </Col>
          {hasO3Model(currentConfig) && (
            <>
              <Divider
                style={{
                  borderColor: "#ECEDEC",
                  margin: props.isModalOpen ? "16px 0" : "24px 0",
                }}
              />
              <Col span={24}>
                <div className="configure-menu-list-item-wrap">
                  <div>
                    <span className="configuration-panel-label">
                      {renderTooltip(
                        "Reasoning Effort:",
                        "Controls the model's reasoning depth and thoroughness",
                        true
                      )}
                    </span>
                  </div>
                  <div>
                    <Select
                      value={currentConfig?.reasoning_effort || "medium"}
                      style={{ width: 120 }}
                      disabled={props.readOnly || isSaving}
                      onChange={(value) => {
                        if (!props.readOnly && !isSaving) {
                          updateConfig({ reasoning_effort: value });
                        }
                      }}>
                      <Select.Option value="low">Low</Select.Option>
                      <Select.Option value="medium">Medium</Select.Option>
                      <Select.Option value="high">High</Select.Option>
                    </Select>
                  </div>
                </div>
              </Col>
            </>
          )}
          <Divider
            style={{
              borderColor: "#ECEDEC",
              margin: props.isModalOpen ? "16px 0" : "24px 0",
            }}
          />
          {currentConfig?.apply_corrections && (
            <Col span={24} style={{ marginBottom: 20 }}>
              <div className="configure-menu-list-item-wrap">
                <div>
                  {renderTooltip(
                    "Safe Mode",
                    "Safe mode prioritizes accuracy over latency",
                    true
                  )}
                </div>
                <div className="switch-btn-wrap">
                  <CustomSwitch
                    className="custom-switch"
                    checked={currentConfig?.safe_mode}
                    disabled={props.readOnly || isSaving}
                    onChange={(checked) => {
                      if (!props.readOnly) {
                        updateConfig({ safe_mode: checked });
                      }
                    }}
                  />
                </div>
              </div>
            </Col>
          )}
        </div>
        {!props.isModalOpen ? (
          <Divider
            type="vertical"
            style={{ borderColor: "#ECEDEC", height: "100%" }}
          />
        ) : (
          <></>
        )}
        <div style={{ width: "100%" }}>
          {!props.isModalOpen ? (
            <Typography.Title
              level={3}
              style={{ margin: "0 0 24px 0", fontWeight: 400 }}>
              Model settings
            </Typography.Title>
          ) : (
            <></>
          )}
          <Col>
            <Col style={{ marginBottom: props.isModalOpen ? 16 : 24 }}>
              <Descriptions
                colon={false}
                layout="vertical"
                className="config-panel-descriptions"
                labelStyle={{
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#616665",
                  marginBottom: 4,
                }}>
                <Descriptions.Item
                  label={renderTooltip(
                    "Model",
                    "The primary AI model to be used for inference",
                    true
                  )}>
                  <ModelInput
                    style={{ width: "100%" }}
                    value={currentConfig?.model}
                    onChange={(value) => {
                      if (!props.readOnly && !isSaving) {
                        updateConfig({ model: value });
                      }
                    }}
                    disabled={props.readOnly || isSaving}
                    label=""
                    intentGroupId={props?.intentGroupId}
                    modelType={
                      currentConfig?.inference_location ===
                      InferenceLocations.CLIENT
                        ? "client"
                        : "server"
                    }
                  />
                </Descriptions.Item>
              </Descriptions>
            </Col>
            {props.allowSecondaryModel !== false && (
              <>
                <Col style={{ marginBottom: props.isModalOpen ? 16 : 24 }}>
                  <Descriptions
                    colon={false}
                    layout="vertical"
                    className="config-panel-descriptions"
                    labelStyle={{
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#616665",
                      marginBottom: 4,
                    }}>
                    <Descriptions.Item
                      label={renderTooltip(
                        "Secondary Model",
                        "The model to fallback to if the primary model is not available or has degraded performance",
                        true
                      )}>
                      <ModelInput
                        style={{ width: "100%" }}
                        value={
                          currentConfig?.fallback_config?.model ??
                          currentConfig?.fallback_model
                        }
                        onChange={(value) => {
                          if (!props.readOnly && !isSaving) {
                            if (value === "") {
                              updateConfig({
                                fallback_config: {
                                  model: "",
                                  strategy: "reactive",
                                  timeout: 0,
                                },
                              });
                            } else {
                              updateConfig({
                                fallback_config: {
                                  ...currentConfig?.fallback_config,
                                  model: value,
                                },
                              });
                            }
                          }
                        }}
                        disabled={props.readOnly || isSaving}
                        label=""
                        intentGroupId={props?.intentGroupId}
                        modelType={
                          currentConfig?.inference_location ===
                          InferenceLocations.CLIENT
                            ? "client"
                            : "server"
                        }
                      />
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
                {(currentConfig?.fallback_config?.model ||
                  currentConfig?.fallback_model) && (
                  <>
                    <Col span={24}>
                      <Descriptions
                        colon={false}
                        column={1}
                        layout="vertical"
                        labelStyle={{
                          fontSize: "16px",
                          fontWeight: 500,
                          color: "#616665",
                          marginBottom: 4,
                        }}>
                        <Descriptions.Item
                          label={renderTooltip(
                            "Fallback Strategy",
                            "Strategy to use when falling back to secondary model",
                            true
                          )}>
                          <Select
                            value={
                              currentConfig?.fallback_config?.strategy ??
                              "reactive"
                            }
                            style={{ width: "100%" }}
                            className="h-56"
                            disabled={props.readOnly || isSaving}
                            onChange={(value) => {
                              updateConfig({
                                fallback_config: {
                                  ...currentConfig?.fallback_config,
                                  strategy: value,
                                  // Clear timeout if strategy is not timeout
                                  timeout:
                                    value === "timeout"
                                      ? currentConfig?.fallback_config?.timeout
                                      : undefined,
                                },
                              });
                            }}>
                            <Select.Option value="reactive">
                              Reactive
                            </Select.Option>
                            <Select.Option value="timeout">
                              Timeout
                            </Select.Option>
                            <Select.Option value="first_response">
                              First Response
                            </Select.Option>
                          </Select>
                        </Descriptions.Item>
                      </Descriptions>
                    </Col>

                    {currentConfig?.fallback_config?.strategy === "timeout" && (
                      <Col span={24} style={{ marginTop: 24 }}>
                        <Descriptions
                          colon={false}
                          column={1}
                          layout="vertical"
                          labelStyle={{
                            fontSize: "16px",
                            fontWeight: 500,
                            color: "#616665",
                            marginBottom: 4,
                          }}>
                          <Descriptions.Item
                            label={renderTooltip(
                              "Fallback Timeout",
                              "Timeout in seconds before falling back to secondary model",
                              true
                            )}>
                            <InputNumber
                              value={currentConfig?.fallback_config?.timeout}
                              min={0}
                              step={0.1}
                              style={{ width: "100%" }}
                              className="h-56"
                              disabled={props.readOnly || isSaving}
                              placeholder="Timeout in seconds"
                              onChange={(value) => {
                                updateConfig({
                                  fallback_config: {
                                    ...currentConfig?.fallback_config,
                                    timeout: value,
                                  },
                                });
                              }}
                            />
                          </Descriptions.Item>
                        </Descriptions>
                      </Col>
                    )}
                  </>
                )}
              </>
            )}
            <Col
              style={{
                marginBottom: props.isModalOpen ? 16 : 24,
                marginTop: 24,
              }}>
              <Descriptions
                colon={false}
                column={1}
                layout="vertical"
                className="custom_slider_wrapper"
                labelStyle={{
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#616665",
                  marginBottom: 4,
                }}>
                <Descriptions.Item
                  label={renderTooltip(
                    "Temperature",
                    "Controls randomness in the model's output (0 = deterministic, 2 = very random)",
                    true
                  )}>
                  <div style={{ width: "100%", position: "relative" }}>
                    <Slider
                      min={0}
                      max={2}
                      className="cus-slider-wrap"
                      disabled={props.readOnly || isSaving}
                      tooltip={{
                        open: tooltipOpen,
                        placement: "top",
                        getPopupContainer: (triggerNode) =>
                          triggerNode.parentElement || document.body,
                      }}
                      value={currentConfig?.temperature}
                      step={0.01}
                      onChange={(value: number) => {
                        if (!props.readOnly && !isSaving) {
                          updateConfig({ temperature: value });
                        }
                      }}
                      onChangeComplete={(value: number) => {
                        if (!props.readOnly && !isSaving) {
                          updateConfig({ temperature: value });
                        }
                      }}
                    />
                  </div>
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Col>
          {isExpanded && (
            <>
              <Typography.Title
                level={3}
                style={{
                  margin: props.isModalOpen ? "0 0 16px 0" : "0 0 32px 0",
                  fontWeight: 400,
                }}>
                Advanced settings
              </Typography.Title>
              <Col style={{ marginBottom: props.isModalOpen ? 16 : 24 }}>
                <Descriptions
                  colon={false}
                  column={1}
                  layout="vertical"
                  labelStyle={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#616665",
                    marginBottom: 4,
                  }}>
                  <Descriptions.Item
                    label={renderTooltip(
                      "Stop Sequence",
                      "Sequences where the API will stop generating further tokens",
                      true
                    )}>
                    <Input
                      className="h-56"
                      placeholder='["", ".", "..."]'
                      disabled={props.readOnly || isSaving}
                      onBlur={(e) => {
                        updateConfig({ stop: e.target.value });
                      }}
                    />
                  </Descriptions.Item>
                </Descriptions>
              </Col>
              <Row
                gutter={24}
                style={{
                  marginBottom: props.isModalOpen ? 16 : 24,
                  flexDirection: props.isModalOpen ? "row-reverse" : "row",
                }}>
                {!props.isModalOpen ? (
                  <Col span={8} className="configure-menu-list-item-wrap">
                    <div className="configure-menu-list-item-wrap w-100">
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: 500,
                          color: "#616665",
                          marginBottom: 4,
                        }}>
                        {renderTooltip(
                          "Log Probs",
                          "Include the log probabilities of the output tokens",
                          true
                        )}
                      </div>
                      <div className="switch-btn-wrap">
                        <CustomSwitch
                          className="custom-switch"
                          checked={currentConfig?.logprobs}
                          disabled={props.readOnly || isSaving}
                          onChange={(checked) => {
                            if (!props.readOnly && !isSaving) {
                              updateConfig({
                                logprobs: checked,
                                apply_corrections: !checked
                                  ? false
                                  : currentConfig?.apply_corrections,
                              });
                            }
                          }}
                        />
                      </div>
                    </div>
                  </Col>
                ) : (
                  <></>
                )}
                <Col span={props?.isModalOpen ? 12 : 8}>
                  <Descriptions
                    colon={false}
                    column={1}
                    layout="vertical"
                    labelStyle={{
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#616665",
                      marginBottom: 4,
                    }}>
                    <Descriptions.Item
                      label={renderTooltip(
                        "N",
                        "How many completions to generate for each prompt",
                        true
                      )}
                      span={props?.isModalOpen ? 6 : 2}>
                      <InputNumber
                        value={currentConfig?.n}
                        min={0}
                        style={{ width: "100%" }}
                        className="h-56"
                        disabled={props.readOnly || isSaving}
                        placeholder="N"
                        onChange={(value) => {
                          if (!props.readOnly && !isSaving && value !== null) {
                            updateConfig({ n: value });
                          }
                        }}
                      />
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
                <Col span={props?.isModalOpen ? 12 : 8}>
                  <Descriptions
                    colon={false}
                    column={1}
                    layout="vertical"
                    labelStyle={{
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#616665",
                      marginBottom: 4,
                    }}>
                    <Descriptions.Item
                      label={renderTooltip(
                        "Max Tokens",
                        "The maximum number of tokens to generate in the completion",
                        true
                      )}
                      span={props?.isModalOpen ? 6 : 2}>
                      <InputNumber
                        value={currentConfig?.max_tokens}
                        min={0}
                        style={{ width: "100%" }}
                        className="h-56 w-100"
                        disabled={props.readOnly || isSaving}
                        placeholder="Tokens"
                        onChange={(value) => {
                          if (!props.readOnly && !isSaving && value !== null) {
                            updateConfig({ max_tokens: value });
                          }
                        }}
                      />
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
              </Row>
              {props.isModalOpen ? (
                <>
                  <Divider
                    style={{ borderColor: "#ECEDEC", margin: "24px 0" }}
                  />
                  <Col className="configure-menu-list-item-wrap">
                    <div className="configure-menu-list-item-wrap w-100">
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: 500,
                          color: "#616665",
                          marginBottom: 4,
                        }}>
                        {renderTooltip(
                          "Log Probs",
                          "Include the log probabilities of the output tokens",
                          true
                        )}
                      </div>
                      <div className="switch-btn-wrap">
                        <CustomSwitch
                          className="custom-switch"
                          checked={currentConfig?.logprobs}
                          disabled={props.readOnly || isSaving}
                          onChange={(checked) => {
                            if (!props.readOnly && !isSaving) {
                              updateConfig({
                                logprobs: checked,
                                apply_corrections: !checked
                                  ? false
                                  : currentConfig?.apply_corrections,
                              });
                            }
                          }}
                        />
                      </div>
                    </div>
                  </Col>
                  <Divider
                    style={{ borderColor: "#ECEDEC", margin: "24px 0" }}
                  />
                </>
              ) : (
                <></>
              )}

              <Col style={{ marginBottom: props.isModalOpen ? 16 : 24 }}>
                <Descriptions
                  colon={false}
                  column={1}
                  layout="vertical"
                  className="custom_slider_wrapper"
                  labelStyle={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#616665",
                    marginBottom: 4,
                  }}>
                  <Descriptions.Item
                    label={renderTooltip(
                      "Presence Penalty",
                      "Increases the model's likelihood to talk about new topics",
                      true
                    )}>
                    <div style={{ width: "100%" }}>
                      <Slider
                        min={-2}
                        max={2}
                        className="cus-slider-wrap"
                        disabled={props.readOnly || isSaving}
                        tooltip={{
                          open: tooltipOpen,
                          placement: "top",
                        }}
                        value={currentConfig?.presence_penalty}
                        step={0.01}
                        onChange={(value: number) => {
                          if (!props.readOnly && !isSaving) {
                            updateConfig({ presence_penalty: value });
                          }
                        }}
                        onChangeComplete={(value: number) => {
                          if (!props.readOnly && !isSaving) {
                            updateConfig({ presence_penalty: value });
                          }
                        }}
                      />
                    </div>
                  </Descriptions.Item>
                </Descriptions>
              </Col>
              {props.isModalOpen ? (
                <Divider style={{ borderColor: "#ECEDEC", margin: "24px 0" }} />
              ) : (
                <></>
              )}
              <Col style={{ marginBottom: props.isModalOpen ? 16 : 24 }}>
                <Descriptions
                  colon={false}
                  column={1}
                  layout="vertical"
                  className="custom_slider_wrapper"
                  labelStyle={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#616665",
                    marginBottom: 4,
                  }}>
                  <Descriptions.Item
                    label={renderTooltip(
                      "Frequency Penalty",
                      "Decreases the model's likelihood to repeat the same line verbatim",
                      true
                    )}>
                    <div style={{ width: "100%" }}>
                      <Slider
                        min={-2}
                        max={2}
                        className="cus-slider-wrap"
                        disabled={props.readOnly || isSaving}
                        tooltip={{ open: tooltipOpen, placement: "top" }}
                        value={currentConfig?.frequency_penalty}
                        step={0.01}
                        onChange={(value: number) => {
                          if (!props.readOnly && !isSaving) {
                            updateConfig({ frequency_penalty: value });
                          }
                        }}
                        onChangeComplete={(value: number) => {
                          if (!props.readOnly && !isSaving) {
                            updateConfig({ frequency_penalty: value });
                          }
                        }}
                      />
                    </div>
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </>
          )}
          <LinkButton
            className="less-more-wrap"
            style={{ padding: 0 }}
            disabled={isSaving}
            icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
            onClick={() => {
              setIsExpanded(!isExpanded);
            }}>
            {isExpanded ? "Hide Advanced Settings" : "Show Advanced Settings"}
          </LinkButton>
        </div>

        {/* Add save button at the bottom */}
        <div
          style={{
            position: "absolute",
            bottom: "20px",
            right: "20px",
            display: "flex",
            gap: "12px",
            alignItems: "center",
          }}>
          {unsavedConfig && (
            <Typography.Text type="warning">
              You have unsaved changes
            </Typography.Text>
          )}
          <Button
            type="primary"
            loading={isSaving}
            disabled={isSaving || !unsavedConfig || props.readOnly}
            onClick={handleSaveChanges}>
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
});

export default ConfigurationPanel;
